import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { useCrmObjectList } from '@/lib/crm/store';
import type { CrmFilter, CrmObject, CrmObjectFilters } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';
import { formatCurrency } from '@/utils/currency';

import { createCRMObjectClient } from '../../core/CRMObjectClient';
import type { ComputedHighlightKind } from '../registry/entityTypeRegistry';
import {
  fetchAggregate,
  readAggregationNumber,
  readAggregationQuantity,
} from './aggregate';
import { formatDuration, formatShortDate } from './formatters';
import { HighlightCardShell } from './HighlightCardShell';
import { iconForComputed } from './highlightIcons';
import type { HighlightResolverResult } from './types';
import { useOpenHighlightTarget } from './useOpenHighlightTarget';

const STALE_TIME = 60_000;

const DASH_RESULT: HighlightResolverResult = {
  value: '—',
  isLoading: false,
  isError: false,
};

const LOADING_RESULT: HighlightResolverResult = {
  value: null,
  isLoading: true,
  isError: false,
};

const ERROR_RESULT: HighlightResolverResult = {
  value: null,
  isLoading: false,
  isError: true,
};

// ---------------------------------------------------------------------------
// Sub-card props
// ---------------------------------------------------------------------------

interface SyncCardProps {
  label: string;
  object: CrmObject;
}

interface AsyncCardProps {
  label: string;
  orgId: number;
  object: CrmObject;
}

// ---------------------------------------------------------------------------
// Sync sub-cards
// ---------------------------------------------------------------------------

function CompanyLocationCard({ label, object }: SyncCardProps) {
  const city = getPropertyValue<string>(object.properties?.ahoy_city);
  const region = getPropertyValue<string>(object.properties?.ahoy_region);
  const country = getPropertyValue<string>(object.properties?.ahoy_country);
  const parts = [city, region, country].filter(Boolean);
  const result: HighlightResolverResult = {
    value: parts.length > 0 ? parts.join(', ') : null,
    isLoading: false,
    isError: false,
  };
  return (
    <HighlightCardShell
      label={label}
      result={result}
      icon={iconForComputed('company_location')}
      emptyText='No location'
    />
  );
}

function MeetingDurationCard({ label, object }: SyncCardProps) {
  const start = getPropertyValue<string>(object.properties?.ahoy_start_time);
  const end = getPropertyValue<string>(object.properties?.ahoy_end_time);
  const result: HighlightResolverResult =
    start && end
      ? { value: formatDuration(start, end), isLoading: false, isError: false }
      : { value: null, isLoading: false, isError: false };
  return (
    <HighlightCardShell
      label={label}
      result={result}
      icon={iconForComputed('meeting_duration')}
      emptyText='Not scheduled'
    />
  );
}

// ---------------------------------------------------------------------------
// Async sub-cards
// ---------------------------------------------------------------------------

function CompanyOpenDealValueCard({ label, orgId, object }: AsyncCardProps) {
  const entityId = object.entity_id;
  const { currentOrg } = useOrg();
  const convertTo = currentOrg?.defaultCurrency;
  const { data, isLoading, isError } = useQuery({
    queryKey: [
      'highlight',
      'computed',
      'company_open_deal_value',
      orgId,
      entityId,
      convertTo,
    ],
    // Closed-stage (won/lost) filtering is not reliably available, so this
    // sums ahoy_amount over ALL deals associated to the company. The card is
    // labeled "Total deal value" in the registry to reflect that.
    queryFn: () =>
      fetchAggregate(orgId, {
        entity_type_name: 'ahoy_deal',
        aggregations: [
          { function: 'sum', property: 'ahoy_amount', alias: 'open_value' },
        ],
        filter_groups: [
          {
            filters: [
              {
                association: 'ahoy_deal_to_company',
                operator: 'exact',
                value: entityId,
              },
            ],
          },
        ],
        ...(convertTo ? { convert_to: convertTo } : {}),
      }),
    staleTime: STALE_TIME,
    enabled: !!orgId && !!entityId,
  });

  let result: HighlightResolverResult;
  if (isLoading) {
    result = LOADING_RESULT;
  } else if (isError) {
    result = ERROR_RESULT;
  } else {
    const { amount, unit } = readAggregationQuantity(
      data?.results ?? [],
      'open_value'
    );
    result = {
      value: amount === 0 ? null : formatCurrency(amount, unit ?? 'USD'),
      isLoading: false,
      isError: false,
    };
  }

  return (
    <HighlightCardShell
      label={label}
      result={result}
      icon={iconForComputed('company_open_deal_value')}
      emptyText='No deals'
    />
  );
}

/** The only property a meeting tile renders; the link needs just the row's id. */
const MEETING_TILE_PROPERTIES = ['ahoy_start_time'];

/**
 * Filters selecting the ONE meeting a tile displays and links to.
 *
 * These replace the `max(ahoy_start_time)` / `min(ahoy_start_time)` aggregates
 * the tiles used to run. An aggregate returns a bare timestamp, and a tile you
 * can click needs the meeting's `entity_id` as well. A `limit: 1` sorted list
 * is the same single round-trip, and going through the CRM store leaves the
 * meeting cached so opening it paints immediately.
 *
 * `ahoy_start_time is not null` reproduces the aggregate's null handling:
 * `max()`/`min()` skip nulls, whereas a plain sort would let a meeting with no
 * start time take the top slot and render an empty value. The upcoming variant
 * gets that for free — a `gte` comparison already excludes nulls. The past
 * variant (`beforeIso`) mirrors this on the upper bound: a `lt` comparison
 * against a future meeting's start time also excludes nulls for free, since
 * SQL `NULL < value` is never true.
 */
function meetingTileFilters(
  association: string,
  entityId: string,
  order: 'asc' | 'desc',
  notBeforeIso?: string,
  beforeIso?: string
): CrmObjectFilters {
  const timeFilters: CrmFilter[] = [];
  if (notBeforeIso) {
    timeFilters.push({
      property: 'ahoy_start_time',
      operator: 'gte',
      value: notBeforeIso,
    });
  }
  if (beforeIso) {
    timeFilters.push({
      property: 'ahoy_start_time',
      operator: 'lt',
      value: beforeIso,
    });
  }
  if (timeFilters.length === 0) {
    // No explicit bound: reproduce the aggregate's null-skipping so a
    // null-start meeting can't take the top sorted slot.
    timeFilters.push({
      property: 'ahoy_start_time',
      operator: 'isnull',
      value: false,
    });
  }
  const filters: CrmFilter[] = [
    { association, operator: 'exact', value: entityId },
    ...timeFilters,
  ];
  return {
    properties: MEETING_TILE_PROPERTIES,
    filter_groups: [{ filters }],
    sort: 'ahoy_start_time',
    order,
    limit: 1,
  };
}

/** Read the tile's display value off the single meeting row, if there is one. */
function meetingTileValue(meeting: CrmObject | undefined): string | null {
  const startTime = meeting
    ? getPropertyValue<string>(meeting.properties?.ahoy_start_time)
    : undefined;
  if (!startTime) {
    return null;
  }
  // formatShortDate returns '—' for unparseable values; map back to null so the
  // shell renders the informative empty text instead of a dash.
  const formatted = formatShortDate(startTime);
  return formatted === '—' ? null : formatted;
}

function ContactLastMetCard({ label, orgId, object }: AsyncCardProps) {
  const entityId = object.entity_id;
  const cutoffIso = upcomingCutoffIso();
  const filters = useMemo(
    () =>
      meetingTileFilters(
        'ahoy_meeting_to_contact',
        entityId,
        'desc',
        undefined,
        cutoffIso
      ),
    [entityId, cutoffIso]
  );
  const listOptions = useMemo(() => ({ enabled: !!entityId }), [entityId]);
  const { data, isLoading, error } = useCrmObjectList(
    orgId,
    'ahoy_meeting',
    filters,
    listOptions
  );

  const meeting = data?.results[0];
  const value = meetingTileValue(meeting);

  let result: HighlightResolverResult;
  if (isLoading) {
    result = LOADING_RESULT;
  } else if (error) {
    result = ERROR_RESULT;
  } else {
    result = { value, isLoading: false, isError: false };
  }

  // Only linkable once a dated meeting actually resolved — "Not met yet" stays
  // an inert label rather than a button that goes nowhere.
  const handleOpen = useOpenHighlightTarget(
    value ? meeting?.entity_id : null,
    'ahoy_meeting'
  );

  return (
    <HighlightCardShell
      label={label}
      result={result}
      icon={iconForComputed('contact_last_met')}
      emptyText='Not met yet'
      onClick={handleOpen}
      actionLabel={handleOpen ? 'Open last meeting' : undefined}
    />
  );
}

/**
 * Maps an entity type to the meeting association used to find its meetings.
 * Returns null for entity types with no meeting association (the card then
 * renders its empty state without firing a query).
 */
function meetingAssociationForEntity(entityTypeName: string): string | null {
  switch (entityTypeName) {
    case 'ahoy_contact':
      return 'ahoy_meeting_to_contact';
    case 'ahoy_company':
      return 'ahoy_meeting_to_company';
    default:
      return null;
  }
}

/**
 * "Upcoming" cutoff, bucketed to the top of the current UTC hour.
 *
 * The cutoff is part of the list query's cache key, so it must be a stable
 * value rather than `new Date().toISOString()` — a fresh timestamp every render
 * would re-key the query on every render and refetch forever. Bucketing keeps
 * the key stable within the hour while still advancing during a long-lived
 * session (the previous aggregate bucketed to the day for the same reason).
 */
function upcomingCutoffIso(): string {
  return `${new Date().toISOString().slice(0, 13)}:00:00.000Z`;
}

function NextInteractionCard({ label, orgId, object }: AsyncCardProps) {
  const entityId = object.entity_id;
  const association = meetingAssociationForEntity(object.entity_type_name);
  const cutoffIso = upcomingCutoffIso();
  const filters = useMemo(
    () => meetingTileFilters(association ?? '', entityId, 'asc', cutoffIso),
    [association, entityId, cutoffIso]
  );
  const listOptions = useMemo(
    // Entity types with no meeting association never fire a request.
    () => ({ enabled: association != null && !!entityId }),
    [association, entityId]
  );
  const { data, isLoading, error } = useCrmObjectList(
    orgId,
    'ahoy_meeting',
    filters,
    listOptions
  );

  const meeting = association == null ? undefined : data?.results[0];
  const value = meetingTileValue(meeting);

  let result: HighlightResolverResult;
  if (association == null) {
    // Entity type has no meeting association — render empty, no query fired.
    result = { value: null, isLoading: false, isError: false };
  } else if (isLoading) {
    result = LOADING_RESULT;
  } else if (error) {
    result = ERROR_RESULT;
  } else {
    result = { value, isLoading: false, isError: false };
  }

  // "Nothing scheduled" must not look clickable — no target, no button.
  const handleOpen = useOpenHighlightTarget(
    value ? meeting?.entity_id : null,
    'ahoy_meeting'
  );

  return (
    <HighlightCardShell
      label={label}
      result={result}
      icon={iconForComputed('next_interaction')}
      emptyText='Nothing scheduled'
      onClick={handleOpen}
      actionLabel={handleOpen ? 'Open next meeting' : undefined}
    />
  );
}

function MeetingPrepStatusCard({ label, orgId, object }: AsyncCardProps) {
  const entityId = object.entity_id;
  const { data, isLoading, isError } = useQuery({
    queryKey: ['highlight', 'computed', 'meeting_prep_status', orgId, entityId],
    queryFn: async () => {
      const client = createCRMObjectClient(orgId, 'ahoy_research');
      return client.list({
        associated_with_entity_id: entityId,
        properties: ['ahoy_status'],
        limit: 1,
      });
    },
    staleTime: STALE_TIME,
    enabled: !!orgId && !!entityId,
  });

  let result: HighlightResolverResult;
  if (isLoading) {
    result = LOADING_RESULT;
  } else if (isError) {
    result = ERROR_RESULT;
  } else {
    result = {
      value: (data?.results?.length ?? 0) > 0 ? 'Ready' : null,
      isLoading: false,
      isError: false,
    };
  }

  return (
    <HighlightCardShell
      label={label}
      result={result}
      icon={iconForComputed('meeting_prep_status')}
      emptyText='Not prepared'
    />
  );
}

function MeetingSummaryStatusCard({ label, orgId, object }: AsyncCardProps) {
  const entityId = object.entity_id;
  const { data, isLoading, isError } = useQuery({
    queryKey: [
      'highlight',
      'computed',
      'meeting_summary_status',
      orgId,
      entityId,
    ],
    queryFn: async () => {
      const client = createCRMObjectClient(orgId, 'ahoy_meeting_notes');
      return client.list({
        associated_with_entity_id: entityId,
        properties: ['ahoy_summary'],
        filter_groups: [
          {
            filters: [
              {
                property: 'ahoy_summary',
                operator: 'isnull',
                value: false,
              },
            ],
          },
        ],
        limit: 1,
      });
    },
    staleTime: STALE_TIME,
    enabled: !!orgId && !!entityId,
  });

  let result: HighlightResolverResult;
  if (isLoading) {
    result = LOADING_RESULT;
  } else if (isError) {
    result = ERROR_RESULT;
  } else {
    const hasSummary = (data?.results?.length ?? 0) > 0;
    result = {
      value: hasSummary ? 'Available' : null,
      isLoading: false,
      isError: false,
    };
  }

  return (
    <HighlightCardShell
      label={label}
      result={result}
      icon={iconForComputed('meeting_summary_status')}
      emptyText='Not summarized'
    />
  );
}

/**
 * Composite touchpoints count for a contact over the last 30 days.
 *
 * Sums:
 *   - `ahoy_meeting` entities associated via `ahoy_meeting_to_contact`
 *     where `ahoy_start_time` is within the trailing 30-day window
 *     (now-30d ≤ start < now) — bounded on both sides so a scheduled
 *     future meeting can't inflate the count
 *   - `ahoy_email_thread` entities associated via
 *     `ahoy_email_thread_to_contact` whose `ahoy_updated_at` ≥ now-30d
 *
 * Note: the email side counts *threads with recent activity*, not
 * individual `ahoy_email_sent`/`ahoy_email_received` events. The aggregate
 * endpoint is entity-scoped, and there is no first-class email-message
 * entity — messages live only as events on a thread. Counting events
 * directly would require a separate event-aggregation endpoint that
 * doesn't exist today; counting threads is the closest approximation
 * available. Each active thread represents at least one email touchpoint
 * within the window, so the number under-counts (vs. one per message)
 * rather than over-counts. This matches Attio's "Engagements" metric.
 *
 * Calls are intentionally omitted — there's no first-class call entity
 * or event type in the schema yet. When that exists, add a third
 * `useQuery` here and roll it into the sum.
 *
 * Runs both queries in parallel; loading and error states surface
 * whichever query is still in flight / has failed.
 */
function ContactTouchpoints30dCard({ label, orgId, object }: AsyncCardProps) {
  const entityId = object.entity_id;
  // Bucket the cutoff to the current UTC day so it advances as the date
  // rolls over during long-lived sessions. Including the bucket in the
  // query key invalidates the cache when the day changes; computing
  // `sinceIso` fresh inside each queryFn ensures the filter value
  // tracks the bucket. The previous `useMemo(..., [])` froze the cutoff
  // at mount, causing the 30d window to drift backward over time.
  const dayBucket = new Date().toISOString().slice(0, 10);

  const meetingsQuery = useQuery({
    queryKey: [
      'highlight',
      'computed',
      'contact_touchpoints_meetings_30d',
      orgId,
      entityId,
      dayBucket,
    ],
    queryFn: () => {
      const sinceIso = new Date(Date.now() - 30 * 86_400_000).toISOString();
      const nowIso = new Date().toISOString();
      return fetchAggregate(orgId, {
        entity_type_name: 'ahoy_meeting',
        aggregations: [{ function: 'count', alias: 'count' }],
        filter_groups: [
          {
            filters: [
              {
                association: 'ahoy_meeting_to_contact',
                operator: 'exact',
                value: entityId,
              },
              {
                property: 'ahoy_start_time',
                operator: 'gte',
                value: sinceIso,
              },
              {
                property: 'ahoy_start_time',
                operator: 'lt',
                value: nowIso,
              },
            ],
          },
        ],
      });
    },
    staleTime: STALE_TIME,
    enabled: !!orgId && !!entityId,
  });

  const emailsQuery = useQuery({
    queryKey: [
      'highlight',
      'computed',
      'contact_touchpoints_emails_30d',
      orgId,
      entityId,
      dayBucket,
    ],
    queryFn: () => {
      const sinceIso = new Date(Date.now() - 30 * 86_400_000).toISOString();
      return fetchAggregate(orgId, {
        entity_type_name: 'ahoy_email_thread',
        aggregations: [{ function: 'count', alias: 'count' }],
        filter_groups: [
          {
            filters: [
              {
                association: 'ahoy_email_thread_to_contact',
                operator: 'exact',
                value: entityId,
              },
              {
                // `ahoy_updated_at` is service-stamped on the thread when
                // the projection processes a new message event, so it
                // tracks recent activity on the thread. The schema does
                // not expose a `last_message_at` property.
                property: 'ahoy_updated_at',
                operator: 'gte',
                value: sinceIso,
              },
            ],
          },
        ],
      });
    },
    staleTime: STALE_TIME,
    enabled: !!orgId && !!entityId,
  });

  let result: HighlightResolverResult;
  if (meetingsQuery.isLoading || emailsQuery.isLoading) {
    result = LOADING_RESULT;
  } else if (meetingsQuery.isError && emailsQuery.isError) {
    // Both failed — surface error. Partial failure falls through to
    // the success branch with whichever query did return, since a
    // missing-zero is less wrong than blanking the card entirely.
    result = ERROR_RESULT;
  } else {
    const meetingCount = readAggregationNumber(
      meetingsQuery.data?.results ?? [],
      'count'
    );
    const emailCount = readAggregationNumber(
      emailsQuery.data?.results ?? [],
      'count'
    );
    const total = meetingCount + emailCount;
    result = {
      value: String(total),
      isLoading: false,
      isError: false,
    };
  }

  return (
    <HighlightCardShell
      label={label}
      result={result}
      icon={iconForComputed('contact_touchpoints_30d')}
    />
  );
}

// ---------------------------------------------------------------------------
// Dispatcher
// ---------------------------------------------------------------------------

export interface ComputedHighlightCardProps {
  label: string;
  compute: ComputedHighlightKind;
  orgId: number;
  object: CrmObject;
}

/**
 * Preferred dispatcher for computed highlight cards.
 *
 * Renders one of six focused sub-components, each of which calls only the
 * hooks it needs — avoiding the wasted queries the monolithic hook approach
 * would incur.
 */
export function ComputedHighlightCard({
  label,
  compute,
  orgId,
  object,
}: ComputedHighlightCardProps) {
  switch (compute) {
    case 'company_location':
      return <CompanyLocationCard label={label} object={object} />;
    case 'meeting_duration':
      return <MeetingDurationCard label={label} object={object} />;
    case 'company_open_deal_value':
      return (
        <CompanyOpenDealValueCard label={label} orgId={orgId} object={object} />
      );
    case 'contact_last_met':
      return <ContactLastMetCard label={label} orgId={orgId} object={object} />;
    case 'next_interaction':
      return (
        <NextInteractionCard label={label} orgId={orgId} object={object} />
      );
    case 'contact_touchpoints_30d':
      return (
        <ContactTouchpoints30dCard
          label={label}
          orgId={orgId}
          object={object}
        />
      );
    case 'meeting_prep_status':
      return (
        <MeetingPrepStatusCard label={label} orgId={orgId} object={object} />
      );
    case 'meeting_summary_status':
      return (
        <MeetingSummaryStatusCard label={label} orgId={orgId} object={object} />
      );
    default: {
      const _exhaustive: never = compute;
      void _exhaustive;
      return <HighlightCardShell label={label} result={DASH_RESULT} />;
    }
  }
}
